import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { ShoppingCart, Plus, Minus, Trash2, CreditCard, SplitSquareVertical, Package, Check, Tag } from 'lucide-react';
import TakeawayUpsell from './TakeawayUpsell';
import TipSelector from './TipSelector';
import PaymentMethodSelector from './PaymentMethodSelector';
import RatingScreen from './RatingScreen';
import { Input } from './ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartDrawer = ({ orders = [], customerId = null }) => {
  const { t } = useLanguage();
  const { cart, cartTotal, cartCount, updateQuantity, removeFromCart, clearCart, tableId, sessionId } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [isSplitMode, setSplitMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showTakeaway, setShowTakeaway] = useState(false);
  const [showTipSelector, setShowTipSelector] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null);
  const [orderNotes, setOrderNotes] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showPaymentMethod, setShowPaymentMethod] = useState(false);
  const [pendingTip, setPendingTip] = useState(0);
  const [showRating, setShowRating] = useState(false);

  // Build order items with unique keys
  const orderItems = orders.flatMap(order =>
    order.items.map((item, idx) => ({
      ...item,
      order_id: order.id,
      is_paid: order.is_fully_paid,
      _key: `${order.id}_${item.item_id}_${idx}`
    }))
  ).filter(item => !item.is_paid);

  // Build cart items with unique keys
  const cartItemsWithKeys = cart.map((item, idx) => ({
    ...item,
    _key: `cart_${item.item_id}_${idx}`
  }));

  const orderTotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalBill = cartTotal + orderTotal;

  // All selectable items (both cart and orders)
  const allItems = [...cartItemsWithKeys, ...orderItems];

  const selectedTotal = selectedKeys.reduce((sum, key) => {
    const item = allItems.find(i => i._key === key);
    return sum + (item ? item.price * item.quantity : 0);
  }, 0);

  const toggleSelection = (key) => {
    setSelectedKeys(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;
    setIsLoading(true);
    try {
      const endpoint = customerId ? `${API}/orders/with-customer?customer_id=${customerId}` : `${API}/orders`;
      const res = await axios.post(endpoint, {
        table_id: tableId,
        session_id: sessionId,
        items: cart.map(item => ({
          item_id: item.item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        notes: orderNotes
      });
      // Apply promo code to the new order if one is entered
      if (promoCode && res.data?.id) {
        try {
          await axios.post(`${API}/promo/apply`, { code: promoCode, order_id: res.data.id });
        } catch (e) { /* promo apply can fail silently */ }
      }
      clearCart();
      setOrderNotes('');
      setPromoCode('');
      setPromoApplied(null);
      window.location.reload();
    } catch (error) {
      console.error('Error placing order:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckout = async (amount, items = null, tipAmount = 0) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/checkout`, {
        table_id: tableId,
        session_id: sessionId,
        amount: amount + tipAmount,
        origin_url: window.location.origin,
        selected_items: items,
        tip_amount: tipAmount
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const initiateCheckout = (amount, items = null) => {
    setPendingCheckout({ amount, items });
    setShowTakeaway(true);
  };

  const handleTakeawayComplete = () => {
    setShowTakeaway(false);
    setShowTipSelector(true);
  };

  const handleTipConfirm = (tipAmount) => {
    setShowTipSelector(false);
    setPendingTip(tipAmount);
    setShowRating(true);
  };

  const handleRatingComplete = () => {
    setShowRating(false);
    setShowPaymentMethod(true);
  };

  const handlePaymentMethodSelect = async (method) => {
    setShowPaymentMethod(false);
    if (method === 'stripe') {
      if (pendingCheckout) {
        handleCheckout(pendingCheckout.amount, pendingCheckout.items, pendingTip);
      }
    } else if (method === 'promptpay' || method === 'counter') {
      // Place order with payment_method noted, no Stripe redirect
      setIsLoading(true);
      try {
        const endpoint = customerId ? `${API}/orders/with-customer?customer_id=${customerId}` : `${API}/orders`;
        await axios.post(endpoint, {
          table_id: tableId,
          session_id: sessionId,
          items: cart.map(item => ({
            item_id: item.item_id, name: item.name, price: item.price, quantity: item.quantity
          })),
          notes: orderNotes,
          payment_method: method
        });
        clearCart();
        setOrderNotes('');
        window.location.reload();
      } catch (error) {
        console.error('Error placing order:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const formatPrice = (price) => `฿${price.toLocaleString()}`;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <button
            className="fixed bottom-20 left-4 right-4 floating-nav py-3 px-4 rounded-full flex items-center justify-between z-50"
            data-testid="cart-button"
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <ShoppingCart className="w-6 h-6 text-[#D4A853]" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#E16833] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </div>
              <span className="font-semibold text-white">{t('view_cart')}</span>
            </div>
            <span className="font-bold text-[#E16833] text-lg">{formatPrice(totalBill)}</span>
          </button>
        </SheetTrigger>

        <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl bg-[#0D0D0D] border-t border-[#333333] flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#333333] flex-shrink-0">
            <SheetTitle className="text-xl font-bold text-white flex items-center justify-between">
              <span>{t('your_order')}</span>
              {(orderItems.length > 0 || cart.length > 0) && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSplitMode(!isSplitMode);
                    setSelectedKeys([]);
                  }}
                  className={`text-sm gap-2 ${isSplitMode ? 'bg-[#D4A853] text-black hover:bg-[#D4A853]/90' : 'text-[#D4A853] hover:bg-[#D4A853]/10'}`}
                  data-testid="split-bill-toggle"
                >
                  <SplitSquareVertical className="w-4 h-4" />
                  {t('split_bill')}
                </Button>
              )}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
            {cart.length === 0 && orderItems.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-16 h-16 mx-auto text-[#333333] mb-4" />
                <p className="text-[#A0A0A0] font-medium">{t('empty_cart')}</p>
                <p className="text-sm text-[#A0A0A0]/70">{t('browse_menu')}</p>
              </div>
            ) : (
              <>
                {/* Cart items (not yet ordered) */}
                {cart.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-[#A0A0A0] mb-2 uppercase tracking-wider">
                      {t('cart')} ({cart.length})
                    </h3>
                    {cartItemsWithKeys.map(item => (
                      <SplitCartItem
                        key={item._key}
                        item={item}
                        isSplitMode={isSplitMode}
                        isSelected={selectedKeys.includes(item._key)}
                        onToggle={() => toggleSelection(item._key)}
                        formatPrice={formatPrice}
                        onUpdateQty={(id, qty) => updateQuantity(id, qty)}
                        onRemove={(id) => removeFromCart(id)}
                        isOrderItem={false}
                      />
                    ))}
                  </div>
                )}

                {/* Order items (already placed) */}
                {orderItems.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-[#A0A0A0] mb-2 uppercase tracking-wider">
                      {t('order_placed')} ({orderItems.length})
                    </h3>
                    {orderItems.map(item => (
                      <SplitCartItem
                        key={item._key}
                        item={item}
                        isSplitMode={isSplitMode}
                        isSelected={selectedKeys.includes(item._key)}
                        onToggle={() => toggleSelection(item._key)}
                        formatPrice={formatPrice}
                        isOrderItem={true}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-[#333333] px-6 pt-4 pb-6 bg-[#0D0D0D]" data-testid="cart-footer">
            {/* Order notes */}
            {cart.length > 0 && (
              <div className="mb-3">
                <Input
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Notes (allergies, sans gluten...)"
                  className="bg-[#1A1A1A] border-[#333333] text-white text-sm rounded-xl placeholder:text-[#666]"
                  data-testid="order-notes-input"
                />
              </div>
            )}

            {/* Promo code */}
            {cart.length > 0 && !promoApplied && (
              <div className="mb-3 flex gap-2">
                <Input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Code promo"
                  className="bg-[#1A1A1A] border-[#333333] text-white text-sm rounded-xl placeholder:text-[#666] flex-1 uppercase"
                  data-testid="promo-code-input"
                />
                <Button
                  onClick={async () => {
                    if (!promoCode) return;
                    setPromoLoading(true);
                    // We can't apply promo before order exists, so just validate visually
                    setPromoApplied({ code: promoCode });
                    setPromoLoading(false);
                  }}
                  disabled={!promoCode || promoLoading}
                  className="bg-[#D4A853] hover:bg-[#C49A48] text-black rounded-xl px-4"
                  data-testid="apply-promo-btn"
                >
                  <Tag className="w-4 h-4" />
                </Button>
              </div>
            )}
            {promoApplied && (
              <div className="mb-3 bg-[#2D6A4F]/20 rounded-xl px-3 py-2 flex items-center justify-between" data-testid="promo-applied">
                <span className="text-sm text-[#2D6A4F] flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Code {promoApplied.code} applique
                </span>
                <button onClick={() => { setPromoApplied(null); setPromoCode(''); }} className="text-[#A0A0A0] text-xs">
                  Retirer
                </button>
              </div>
            )}

            <div className="w-full space-y-2 mb-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#A0A0A0]">{t('subtotal')}</span>
                <span className="font-medium text-white">{formatPrice(cartTotal)}</span>
              </div>
              {orderTotal > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#A0A0A0]">{t('order_placed')}</span>
                  <span className="font-medium text-white">{formatPrice(orderTotal)}</span>
                </div>
              )}
              {isSplitMode && selectedKeys.length > 0 && (
                <div className="flex justify-between text-sm text-[#D4A853] font-medium">
                  <span>{t('pay_selected')} ({selectedKeys.length})</span>
                  <span className="font-bold">{formatPrice(selectedTotal)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-[#333333]">
                <span className="text-white">{t('total')}</span>
                <span className="text-[#D4A853]">{formatPrice(totalBill)}</span>
              </div>
            </div>

            <div className="w-full flex flex-col gap-2">
              {cart.length > 0 && (
                <Button
                  onClick={handlePlaceOrder}
                  disabled={isLoading}
                  className="w-full h-12 btn-secondary rounded-full text-base font-semibold"
                  data-testid="place-order-btn"
                >
                  <Package className="w-5 h-5 mr-2" />
                  {t('place_order')}
                </Button>
              )}

              {(orderTotal > 0 || cart.length > 0) && (
                <>
                  {isSplitMode ? (
                    <Button
                      onClick={() => {
                        const selectedItemIds = selectedKeys.map(k => {
                          const item = allItems.find(i => i._key === k);
                          return item?.item_id;
                        }).filter(Boolean);
                        initiateCheckout(selectedTotal, selectedItemIds);
                      }}
                      disabled={isLoading || selectedKeys.length === 0 || selectedTotal === 0}
                      className="w-full h-12 bg-[#D4A853] hover:bg-[#C49A48] text-black rounded-full text-base font-semibold disabled:opacity-40"
                      data-testid="pay-selected-btn"
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      {selectedKeys.length > 0
                        ? `${t('pay_selected')} - ${formatPrice(selectedTotal)}`
                        : t('pay_selected')
                      }
                    </Button>
                  ) : (
                    <Button
                      onClick={() => initiateCheckout(totalBill)}
                      disabled={isLoading || totalBill === 0}
                      className="w-full h-12 btn-primary rounded-full text-base font-semibold"
                      data-testid="pay-full-btn"
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      {t('pay_full')} - {formatPrice(totalBill)}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <TakeawayUpsell
        isOpen={showTakeaway}
        onClose={() => setShowTakeaway(false)}
        onComplete={handleTakeawayComplete}
      />

      <TipSelector
        isOpen={showTipSelector}
        onClose={() => setShowTipSelector(false)}
        onConfirm={handleTipConfirm}
        billAmount={pendingCheckout?.amount || 0}
      />

      <RatingScreen
        isOpen={showRating}
        onClose={() => setShowRating(false)}
        onComplete={handleRatingComplete}
        tableId={tableId}
        customerId={customerId}
      />

      <PaymentMethodSelector
        isOpen={showPaymentMethod}
        onClose={() => setShowPaymentMethod(false)}
        onSelect={handlePaymentMethodSelect}
        billAmount={(pendingCheckout?.amount || 0) + pendingTip}
        formatPrice={formatPrice}
      />
    </>
  );
};

// Extracted as a separate component (not recreated each render)
const SplitCartItem = ({ item, isSplitMode, isSelected, onToggle, formatPrice, onUpdateQty, onRemove, isOrderItem }) => (
  <div
    className={`flex items-center gap-3 py-3 border-b border-[#333333] last:border-b-0 transition-colors ${isSelected ? 'bg-[#D4A853]/10 -mx-2 px-2 rounded-lg' : ''}`}
    data-testid={`cart-row-${item._key}`}
  >
    {isSplitMode && (
      <button
        onClick={onToggle}
        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
          isSelected
            ? 'bg-[#D4A853] border-[#D4A853]'
            : 'border-[#666666] hover:border-[#D4A853]'
        }`}
        data-testid={`select-item-${item._key}`}
      >
        {isSelected && <Check className="w-4 h-4 text-black" />}
      </button>
    )}
    {item.image_url && (
      <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
    )}
    <div className="flex-1 min-w-0">
      <p className="font-medium text-sm text-white truncate">{item.name}</p>
      <p className="text-[#D4A853] font-semibold text-sm">{formatPrice(item.price)}</p>
    </div>
    {!isOrderItem ? (
      <div className="flex items-center gap-1">
        <Button
          onClick={() => onUpdateQty(item.item_id, item.quantity - 1)}
          className="w-6 h-6 rounded-full p-0 bg-[#252525] hover:bg-[#333333]"
          variant="ghost"
        >
          <Minus className="w-3 h-3" />
        </Button>
        <span className="w-6 text-center text-sm font-medium text-white">{item.quantity}</span>
        <Button
          onClick={() => onUpdateQty(item.item_id, item.quantity + 1)}
          className="w-6 h-6 rounded-full p-0 bg-[#252525] hover:bg-[#333333]"
          variant="ghost"
        >
          <Plus className="w-3 h-3" />
        </Button>
        <Button
          onClick={() => onRemove(item.item_id)}
          className="w-6 h-6 rounded-full p-0 text-[#E85A2C] hover:bg-[#E85A2C]/10"
          variant="ghost"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    ) : (
      <span className="text-sm text-[#A0A0A0]">x{item.quantity}</span>
    )}
  </div>
);

export default CartDrawer;
